import React, { useState, useRef, useEffect } from 'react'
import { useVehicles } from '../contexts/VehicleContext'
import { useTranslation } from 'react-i18next'
import { Search, Settings, Bell, User, RefreshCw, Globe, ChevronDown } from 'lucide-react'

const Header: React.FC = () => {
  const { state, toggleRealTime, loadVehicles } = useVehicles()
  const { t, i18n } = useTranslation()
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    setLangOpen(false)
  }

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-cvs-red rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm">CVS</span>
            </div>
            <h1 className="text-xl font-semibold text-gray-800">{t('fleet_portal')}</h1>
          </div>
        </div>

        <div className="flex-1 max-w-md mx-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={toggleRealTime}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              state.realTimeEnabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${
              state.realTimeEnabled ? 'bg-green-500' : 'bg-gray-400'
            }`} />
            <span className="text-sm font-medium">
              {state.realTimeEnabled ? t('live') : t('paused')}
            </span>
          </button>

          <button
            onClick={() => loadVehicles()}
            disabled={state.loading}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${state.loading ? 'animate-spin' : ''}`} />
          </button>

          <button className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
            <Bell className="w-5 h-5" />
            {state.alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {state.alerts.length}
              </span>
            )}
          </button>

          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center space-x-1.5 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm font-medium uppercase">{i18n.language?.substring(0, 2) || 'en'}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                <button
                  onClick={() => switchLanguage('en')}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                    i18n.language?.startsWith('en') ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>EN</span>
                  <span>{t('english')}</span>
                </button>
                <button
                  onClick={() => switchLanguage('fr')}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                    i18n.language?.startsWith('fr') ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>FR</span>
                  <span>{t('french')}</span>
                </button>
              </div>
            )}
          </div>

          <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
            <Settings className="w-5 h-5" />
          </button>

          <div className="flex items-center space-x-2">
            <button className="flex items-center space-x-2 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
              <User className="w-5 h-5" />
              <span className="text-sm font-medium">Admin</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          <span>{t('vehicles')}: {state.vehicles.length}</span>
          <span>{t('in_movement')}: {state.vehicles.filter(v => v.status === 'moving').length}</span>
          <span>{t('stopped_count')}: {state.vehicles.filter(v => v.status === 'stopped').length}</span>
          <span>{t('no_connection')}: {state.vehicles.filter(v => v.status === 'offline').length}</span>
        </div>
        <div className="text-xs text-gray-500">
          {t('last_update')}: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </header>
  )
}

export default Header
